import React, { useState, useCallback, useEffect } from 'react'
import { resolveKindComponent } from '../../../cards/preparation/kinds/resolveKind.js'
import { CardEditor } from '../../../cards/preparation/CardEditor.jsx'
import { Button } from '../../../ui'
import { appsyncQuery } from '../../../api/appsync-client.js'

// ─── GraphQL for the WantToDiscussToggle (manager view) ──────────────────────

const GET_SESSION_ROWS_TPS = `
  query GetSessionRows($sessionId: ID!) {
    getSessionRows(sessionId: $sessionId) {
      talkingPoints {
        talkingPointId
        sessionId
        text
        status
        sourceBulletId
        addedBy
      }
    }
  }
`

const ADD_TALKING_POINT = `
  mutation AddTalkingPoint($sessionId: ID!, $input: AddTalkingPointInput!) {
    addTalkingPoint(sessionId: $sessionId, input: $input) {
      talkingPointId
      sessionId
      text
      status
      sourceBulletId
      addedBy
    }
  }
`

const REMOVE_TALKING_POINT = `
  mutation RemoveTalkingPoint($sessionId: ID!, $talkingPointId: ID!) {
    removeTalkingPoint(sessionId: $sessionId, talkingPointId: $talkingPointId)
  }
`

// ─── WantToDiscussToggle ─────────────────────────────────────────────────────

/**
 * Toggle for marking a prep bullet "Want to discuss" (manager view only).
 * Preserved from ManagerPrepReadPage — copy and aria-labels unchanged.
 */
function WantToDiscussToggle({ bulletId, bulletText, talkingPointId, onToggle, loading }) {
  const isMarked = Boolean(talkingPointId)
  const label = isMarked ? 'Marked for discussion' : 'Want to discuss'
  const ariaLabel = isMarked
    ? 'Remove from discussion'
    : 'Mark this item to discuss in the meeting'

  return (
    <button
      type="button"
      className={`want-to-discuss${isMarked ? ' want-to-discuss--on' : ''}`}
      aria-pressed={isMarked}
      aria-label={ariaLabel}
      onClick={() => onToggle(bulletId, bulletText, talkingPointId)}
      disabled={loading}
      title={ariaLabel}
    >
      {label}
    </button>
  )
}

// ─── PrepBulletRow ────────────────────────────────────────────────────────────

function PrepBulletRow({ bullet, talkingPointId, onToggle, toggling }) {
  return (
    <div className="manager-prep__bullet">
      <span className="manager-prep__bullet-text">{bullet.text}</span>
      <WantToDiscussToggle
        bulletId={bullet.bulletId}
        bulletText={bullet.text}
        talkingPointId={talkingPointId}
        onToggle={onToggle}
        loading={toggling === bullet.bulletId}
      />
    </div>
  )
}

// Card kind titles for manager read view (mirror of ManagerPrepReadPage)
const CARD_TITLES = {
  'prep-energy': 'Energy meter',
  'prep-week-rating': 'Week rating',
  'prep-wins': 'Wins',
  'prep-learnings': 'Learnings',
  'prep-risks': 'Risks',
  'prep-asks': 'Asks for your manager',
  'prep-focus': 'Focus areas',
  'prep-priorities': 'Priorities',
  'prep-mood': 'Mood',
  'prep-wlb': 'Reflection — W / L / B',
  'prep-kudos': 'Kudos',
  'prep-feedback': 'Feedback',
  'prep-stuck': 'Stuck on',
  'prep-ssc': 'Start / stop / continue',
  'prep-support': 'Support needed',
  'prep-one-word': 'One word',
  'prep-gratitude': 'Gratitude',
  'prep-progress-updates': 'Progress updates',
  'prep-workload': 'Workload',
  'prep-clarity': 'Clarity check',
}

// ─── ManagerPrepView ──────────────────────────────────────────────────────────

/**
 * Manager's read-only view of the report's prep.
 * Contains WantToDiscussToggle per bullet — preserved from ManagerPrepReadPage.
 */
function ManagerPrepView({ prep, reportName, sessionId, me }) {
  const displayName = reportName || 'Your report'

  // bulletId → talkingPointId (for "marked" state tracking)
  const [bulletTpMap, setBulletTpMap] = useState({})
  const [toggling, setToggling] = useState(null)
  const [tpsLoading, setTpsLoading] = useState(true)

  // Pre-fetch existing TPs so the on-state is correct on first load
  useEffect(() => {
    if (!sessionId) { setTpsLoading(false); return }
    setTpsLoading(true)
    appsyncQuery(GET_SESSION_ROWS_TPS, { sessionId })
      .then((data) => {
        const tps = data?.getSessionRows?.talkingPoints ?? []
        const map = {}
        tps.forEach((tp) => {
          if (tp.sourceBulletId) map[tp.sourceBulletId] = tp.talkingPointId
        })
        setBulletTpMap(map)
      })
      .catch(() => {})
      .finally(() => setTpsLoading(false))
  }, [sessionId])

  const handleToggle = useCallback(
    async (bulletId, bulletText, existingTpId) => {
      setToggling(bulletId)
      try {
        if (existingTpId) {
          await appsyncQuery(REMOVE_TALKING_POINT, { sessionId, talkingPointId: existingTpId })
          setBulletTpMap((prev) => {
            const next = { ...prev }
            delete next[bulletId]
            return next
          })
        } else {
          const data = await appsyncQuery(ADD_TALKING_POINT, {
            sessionId,
            input: { text: bulletText, sourceBulletId: bulletId, addedBy: me?.userId },
          })
          const tp = data?.addTalkingPoint
          if (tp) setBulletTpMap((prev) => ({ ...prev, [bulletId]: tp.talkingPointId }))
        }
      } catch {
        // Silently fail; user can retry
      } finally {
        setToggling(null)
      }
    },
    [sessionId, me],
  )

  // Empty state: no prep at all
  if (!prep || !prep.prep) {
    return (
      <div className="manager-prep manager-prep--empty" data-testid="manager-prep-no-prep">
        <h2 className="manager-prep__heading">{displayName}&apos;s prep</h2>
        <p className="manager-prep__empty-state">
          {displayName} hasn&apos;t added any prep for this session yet. Come back closer to the
          meeting, or go in without a preview — you can still run the session.
        </p>
      </div>
    )
  }

  // Prep exists but not submitted yet
  if (!prep.prep.isVisibleToManager) {
    return (
      <div className="manager-prep manager-prep--not-submitted" data-testid="manager-prep-not-visible">
        <h2 className="manager-prep__heading">{displayName}&apos;s prep</h2>
        <p className="manager-prep__empty-state">
          {displayName} has a session open but hasn&apos;t filled in any cards yet.
        </p>
      </div>
    )
  }

  const cards = (prep.cards ?? []).slice().sort((a, b) => a.order - b.order)
  const bullets = prep.bullets ?? []
  const allBulletsEmpty = bullets.length === 0

  return (
    <div className="manager-prep" data-testid="manager-prep-view">
      <div className="manager-prep__header">
        <h2 className="manager-prep__heading">{displayName}&apos;s prep</h2>
        <p className="manager-prep__helper">
          This is {displayName}&apos;s prep — you can read it but not edit it. Mark the items you
          want to cover in the meeting.
        </p>
      </div>

      {allBulletsEmpty ? (
        <p className="manager-prep__empty-state" data-testid="manager-prep-empty-bullets">
          {displayName} has a session open but hasn&apos;t filled in any cards yet.
        </p>
      ) : (
        <div className="manager-prep__cards" data-testid="manager-prep-cards">
          {cards.map((card) => {
            const cardBullets = bullets
              .filter((b) => b.prepCardId === card.prepCardId)
              .sort((a, b) => a.order - b.order)
            const title = CARD_TITLES[card.kind] || card.kind
            const isEmpty = cardBullets.length === 0

            return (
              <section
                key={card.prepCardId}
                className="manager-prep__card"
                aria-label={title}
              >
                <h3 className="manager-prep__card-title">{title}</h3>
                {isEmpty ? (
                  <p className="manager-prep__card-empty">No entries yet.</p>
                ) : (
                  <ul className="manager-prep__bullets">
                    {cardBullets.map((bullet) => (
                      <li key={bullet.bulletId}>
                        <PrepBulletRow
                          bullet={bullet}
                          talkingPointId={tpsLoading ? undefined : (bulletTpMap[bullet.bulletId] ?? null)}
                          onToggle={handleToggle}
                          toggling={toggling}
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ─── PrepSection ─────────────────────────────────────────────────────────────

/**
 * PrepSection — renders the prep card grid or the manager read view.
 *
 * Report view (isManagerView=false): prep card grid + CardEditor.
 * The "Share with manager" button is in the canvas page's SectionDivider action
 * slot; this section just renders the grid. The parent passes `prep`, `loading`,
 * `error`, `reload`, `updatePrepCard` so it does not duplicate the usePrep call.
 *
 * Manager view (isManagerView=true): ManagerPrepView with WantToDiscussToggle.
 *
 * Props:
 *   prep            object|null — prep tree from usePrep
 *   loading         boolean
 *   error           Error|null
 *   reload          function
 *   updatePrepCard  function
 *   isManagerView   boolean
 *   disabled        boolean — true when prep is locked
 *   reportName      string  — display name for manager heading
 *   sessionId       string  — used for talking-point mutations in manager view
 *   me              object  — current user
 */
export function PrepSection({
  prep,
  loading,
  error,
  reload,
  updatePrepCard,
  isManagerView = false,
  disabled = false,
  reportName,
  sessionId,
  ownerUserId,
  me,
}) {

  const [editorOpen, setEditorOpen] = useState(false)
  const [editorCard, setEditorCard] = useState(null)

  const openEditor = useCallback((card) => {
    setEditorCard(card)
    setEditorOpen(true)
  }, [])

  const closeEditor = useCallback(() => {
    setEditorOpen(false)
    setEditorCard(null)
  }, [])

  const handleCardChange = useCallback(async (card, newValue) => {
    await updatePrepCard(card.prepCardId, { state: JSON.stringify(newValue) })
  }, [updatePrepCard])

  if (loading) {
    return (
      <div className="prep-section__loading" aria-live="polite" data-testid="prep-section-loading">
        <span>Loading prep…</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="prep-section__error" role="alert" data-testid="prep-section-error">
        <p>Failed to load prep.</p>
        <Button variant="secondary" onClick={reload}>Retry</Button>
      </div>
    )
  }

  // Manager view — show the manager read-only prep with WantToDiscuss toggles
  if (isManagerView) {
    return (
      <div className="prep-section prep-section--manager" data-testid="prep-section-manager">
        <ManagerPrepView
          prep={prep}
          reportName={reportName}
          sessionId={sessionId}
          me={me}
        />
      </div>
    )
  }

  if (!prep) {
    return (
      <div className="prep-section__empty" data-testid="prep-section-no-prep">
        <p className="viewing-notice">No prep found for this session.</p>
      </div>
    )
  }

  const cards = [...(prep.cards ?? [])].sort((a, b) => a.order - b.order)
  const isSubmitted = !!prep.submittedAt
  const cardDisabled = disabled || isSubmitted

  return (
    <div className="prep-section" data-testid="prep-section">
      {isSubmitted && (
        <div className="viewing-notice" data-testid="prep-submitted-badge">
          Shared with manager
        </div>
      )}

      <div className="cards-grid" role="list" aria-label="Prep cards" data-testid="prep-cards-grid">
        {cards.map((card) => {
          const Component = resolveKindComponent(card.kind)
          if (!Component) return null

          let value = null
          try { value = card.state ? JSON.parse(card.state) : null } catch { value = null }

          return (
            <div
              key={card.prepCardId}
              role="listitem"
              className="cards-grid__item"
              onClick={() => !cardDisabled && openEditor(card)}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && !cardDisabled) {
                  e.preventDefault()
                  openEditor(card)
                }
              }}
              tabIndex={cardDisabled ? undefined : 0}
              aria-label={`${card.kind} card${cardDisabled ? ', read-only' : ''}`}
              data-testid={`prep-card-${card.kind}`}
            >
              <Component
                card={card}
                value={value}
                onChange={(newValue) => handleCardChange(card, newValue)}
                disabled={cardDisabled}
                ownerUserId={ownerUserId}
              />
            </div>
          )
        })}
      </div>

      {editorCard && (
        <CardEditor
          open={editorOpen}
          onClose={closeEditor}
          card={editorCard}
          value={(() => {
            const c = cards.find((c) => c.prepCardId === editorCard.prepCardId)
            try { return c?.state ? JSON.parse(c.state) : null } catch { return null }
          })()}
          onChange={(newValue) => handleCardChange(editorCard, newValue)}
          disabled={cardDisabled}
          ownerUserId={ownerUserId}
        />
      )}
    </div>
  )
}
